// Zod schemas + inferred TypeScript types for all on-disk Avatar config files.
// Centralised so commands import from one place and validation matches storage.
import { z } from "zod";

// ~/.avatar/config.json — OAuth credentials after `avatar login`.
// chmod 600 enforced at write site.
export const userConfigSchema = z.object({
  // zod 4: format validators chuyển thành top-level (z.email/z.iso.datetime).
  // .string().email()/.datetime() vẫn chạy nhưng deprecated — dùng API mới.
  email: z.email(),
  name: z.string(),
  access_token: z.string().min(1),
  refresh_token: z.string().min(1),
  expires_at: z.iso.datetime(),
  id_token: z.string().min(1),
});
export type UserConfig = z.infer<typeof userConfigSchema>;

// ~/.avatar/state.json — non-credential user state (tool install records,
// allowed-paths chosen for filesystem MCP, etc).
export const userStateSchema = z.object({
  installed_tools: z
    .record(
      z.string(),
      z.object({
        version: z.string().optional(),
        installed_at: z.iso.datetime(),
        install_method: z.string(),
      }),
    )
    .default({}),
  tool_inputs: z.record(z.string(), z.unknown()).default({}),
});
export type UserState = z.infer<typeof userStateSchema>;

// .claude/settings.json — per-project Claude Code settings emitted by `avatar init`.
// Schema migrated v1.5.0 from legacy `allowedTools` to Claude Code modern schema
// (permissions.allow/deny + hooks per-event). Pack template merging targets this shape.
export const projectSettingsSchema = z.object({
  $schema: z.string().optional(),
  includeCoAuthoredBy: z.boolean().optional(),
  env: z.record(z.string(), z.string()).default({}),
  permissions: z
    .object({
      allow: z.array(z.string()).default([]),
      deny: z.array(z.string()).default([]),
    })
    .partial()
    .optional(),
  // Hooks shape per Claude Code spec: each event key maps to array of matcher entries.
  // We accept unknown body since pack/template control concrete schema; Zod just guards
  // top-level structure to avoid corrupting user file on merge.
  hooks: z.record(z.string(), z.array(z.unknown())).optional(),
  statusLine: z
    .object({
      type: z.string(),
      command: z.string(),
      padding: z.number().optional(),
    })
    .optional(),
});
export type ProjectSettings = z.infer<typeof projectSettingsSchema>;

// Init mode — determines workspace topology (Chapter 02 + Chapter 07 spec).
export const initModeSchema = z.enum(["internal", "client", "library"]);
export type InitMode = z.infer<typeof initModeSchema>;
